import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import api from "../services/api";

export const fetchQr = createAsyncThunk('qr/fetchQr', async (_, thunkAPI) => {
  try {
    const response = await api.get('/qr');
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch QR code";
    // Critical: Keep error logging for QR code fetch failures
    if (process.env.NODE_ENV === 'development') {
      console.error('QR Fetch Error:', errorMessage);
    }
    return thunkAPI.rejectWithValue(errorMessage);
  }
});


// Get authentication status
export const getAuthStatus = createAsyncThunk(
  "qr/getAuthStatus",
  async (userId, thunkAPI) => {
    try {
      const response = await api.get(`/qr/status`, {
        params: { userId },
      });
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to get auth status";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Activate all sessions (admin only)
export const activateAllSessions = createAsyncThunk(
  "qr/activateAllSessions",
  async (_, thunkAPI) => {
    try {
      const response = await api.get("/activateAllSessions");
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to activate all sessions";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Activate user's own session
export const activateUserownSession = createAsyncThunk(
  "qr/activateUserownSession",
  async (id, thunkAPI) => {
    try {
      const response = await api.get(`/activateUserSession/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to activate user session";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Logout from WhatsApp
export const logout = createAsyncThunk(
  "qr/logout",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.post("/wp/logout", { userId });
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Logout failed";
      return rejectWithValue(errorMessage);
    }
  }
);

// Force reinitialize WhatsApp client
export const forceReinitialize = createAsyncThunk(
  "qr/forceReinitialize",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post("/qr/reinitialize");
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Reinitialization failed";
      return rejectWithValue(errorMessage);
    }
  }
);

const qrSlice = createSlice({
  name: "qr",
  initialState: {
    qrCode: null,
    clientReady: false,
    loading: false,
    error: null,
    authStatus: {
      ready: false,
      initialized: false,
      sessionExists: false,
      qrExpiresInSeconds: null,
      hasQr: false,
    },
    activateAllSessionsData: null,
    activateUserSessionData: null,
    qrExpiresInSeconds: null,
  },
  reducers: {
    clearQr: (state) => {
      state.qrCode = null;
      state.clientReady = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setClientReady: (state, action) => {
      state.clientReady = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchQr
      .addCase(fetchQr.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQr.fulfilled, (state, action) => {
        state.loading = false;
        // IMPORTANT: Handle null QR when client is ready
        state.qrCode = action.payload.qr || null;
        state.clientReady = action.payload.clientReady || false;
        state.qrExpiresInSeconds = action.payload.qrExpiresInSeconds || null;
        
        // Show success message when client becomes ready
        if (action.payload.clientReady && !state.clientReady) {
          toast.success("WhatsApp connected successfully!");
        } else if (action.payload.qr) {
          // Only show toast for QR fetch, not every time
          // toast.success("QR code fetched successfully!");
        }
      })
      .addCase(fetchQr.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch QR code";
        toast.error(`QR fetch failed: ${state.error}`);
      })

      // getAuthStatus
      .addCase(getAuthStatus.pending, (_state) => {
        // Don't set loading to true for status checks to avoid UI flicker
      })
      .addCase(getAuthStatus.fulfilled, (state, action) => {
        state.authStatus = {
          ready: action.payload.ready || false,
          initialized: action.payload.initialized || false,
          sessionExists: action.payload.sessionExists || false,
          qrExpiresInSeconds: action.payload.qrExpiresInSeconds || null,
          hasQr: action.payload.hasQr || false,
        };
        state.clientReady = action.payload.ready || false;
        state.qrExpiresInSeconds = action.payload.qrExpiresInSeconds || null;
      })
      .addCase(getAuthStatus.rejected, (state, action) => {
        state.error = action.payload || "Failed to get auth status";
      })

      // activateAllSessions
      .addCase(activateAllSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateAllSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.activateAllSessionsData = action.payload;
        toast.success(
          `Activated ${action.payload.activated} out of ${action.payload.total} sessions!`
        );
      })
      .addCase(activateAllSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to activate all sessions";
        toast.error(`Activating all sessions failed: ${state.error}`);
      })

      // activateUserownSession
      .addCase(activateUserownSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateUserownSession.fulfilled, (state, action) => {
        state.loading = false;
        state.activateUserSessionData = action.payload;
        state.clientReady = true;
        toast.success("Session activated successfully!");
      })
      .addCase(activateUserownSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to activate user session";
        toast.error(`Activating session failed: ${state.error}`);
      })

      // logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.qrCode = null;
        state.clientReady = false;
        state.activateUserSessionData = null;
        state.authStatus = {
          ready: false,
          initialized: false,
          sessionExists: false,
          qrExpiresInSeconds: null,
          hasQr: false,
        };
        state.error = null;
        toast.success("Logged out successfully!");
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Logout failed";
        toast.error(`Logout failed: ${state.error}`);
      })

      // forceReinitialize
      .addCase(forceReinitialize.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forceReinitialize.fulfilled, (state) => {
        state.loading = false;
        state.qrCode = null;
        state.clientReady = false;
        state.error = null;
        toast.success("Client reinitialization started!");
      })
      .addCase(forceReinitialize.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Reinitialization failed";
        toast.error(`Reinitialization failed: ${state.error}`);
      });
  },
});

export const { clearQr, clearError, setClientReady } = qrSlice.actions;
export default qrSlice.reducer;
